import { c as _c } from "react/compiler-runtime";
/**
 * Dark, image-led result presentation for a generated day plan, in two
 * draggable layouts that share all logic (reorder, live time recompute,
 * calendar export, share):
 *
 *  - "timeline": v0's Board design rebased onto our design system — a gold
 *    time-rail with arrival nodes, full-bleed photographic stop cards with a
 *    coloured tag chip + per-card calendar actions, dashed walk connectors,
 *    and an "end of day" marker. The default results view.
 *  - "mosaic":   a magazine spread — the first stop promoted to a full-bleed
 *    hero, the rest in a reorderable image grid.
 *
 * Reordering is client-side only — the saved KV plan is an immutable snapshot.
 * Persisting a reordered plan would need a new server action (out of scope).
 */
import { createContext, useContext, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { createPortal } from "react-dom";
import Image from "next/image";
import { Reorder, useDragControls, useReducedMotion } from "framer-motion";
import { ArrowUpRight, CalendarPlus, Check, Clock, Download, Footprints, GripVertical, LifeBuoy, Link2, Loader2, MapPin, Navigation, RefreshCw, RotateCcw, Search, ShoppingBag, Smartphone, X } from "lucide-react";
import { DateTime } from "luxon";
import posthog from "posthog-js";
import { Button } from "@repo/ui/button";
import { Overline } from "@repo/ui/overline";
import { Tag } from "@repo/ui/tag";
import { searchClient } from "@/lib/algolia/client";
import { getAlgoliaIndexName } from "@/lib/algolia/config";
import { haversineKm, walkMinutes } from "@/lib/concierge/distance";
import { WeatherWidget } from "./concierge-wizard-cinematic";
import { AddToHomeScreen, loadedAtCurrentUrl, promptInstall, reloadForHomeScreen, shouldOfferHomeScreen, takeHomeScreenReload, useHomeScreenPlatform, usePlanManifest } from "./add-to-home-screen";
import { buildPlanIcs, googleCalendarUrl, PRECINCT_ZONE } from "@/lib/concierge/plan-calendar";
import { mapsUrl } from "@/components/tenant-detail/tenant-detail-intro/maps-url";
import type { DayPlan, PlannablePlace, PlannedStop } from "@/lib/concierge/types";
export type BoardVariant = "timeline" | "mosaic";
interface BoardStop {
  key: string;
  stop: PlannedStop;
}
function toBoardStops(stops: PlannedStop[]): BoardStop[] {
  return stops.map((stop, i) => ({
    key: `${stop.place.id}-${i}`,
    stop
  }));
}

/**
 * Recompute arrival times for the current order. Anchors to the earliest
 * arrival time in the original plan, then walks the list adding each stop's
 * dwell plus the walk from the previous stop's coordinates.
 */
function resequence(board: BoardStop[], baseDate: string): PlannedStop[] {
  if (board.length === 0) return [];
  const firstArrival = board.map(b => b.stop.arrivalTime).sort()[0]!;
  let cursor = DateTime.fromISO(`${baseDate}T${firstArrival}`, {
    zone: PRECINCT_ZONE
  });
  return board.map(({
    stop
  }, i) => {
    const prev = board[i - 1]?.stop;
    const walk = prev ? Math.round(walkMinutes(haversineKm(prev.place.geopoint, stop.place.geopoint))) : 0;
    cursor = cursor.plus({
      minutes: walk
    });
    const arrivalTime = cursor.toFormat("HH:mm");
    cursor = cursor.plus({
      minutes: stop.dwellMinutes
    });
    return {
      ...stop,
      arrivalTime,
      walkFromPreviousMinutes: walk
    };
  });
}
function formatMinutes(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (h === 0) return `${m} min`;
  if (m === 0) return `${h} hr`;
  return `${h} hr ${m} min`;
}

/** ISO date → "Tue, 30 Jun" for display in the header. */
function formatPlanDate(iso: string): string {
  const d = DateTime.fromISO(iso, {
    zone: PRECINCT_ZONE
  });
  return d.isValid ? d.toFormat("ccc, d LLL") : iso;
}

/** "14:30" → { time: "2:30", ampm: "pm" } for display. */
function to12h(hhmm: string): {
  time: string;
  ampm: string;
} {
  const [h, m] = hhmm.split(":").map(Number);
  const ampm = (h ?? 0) >= 12 ? "pm" : "am";
  const disp = (h ?? 0) > 12 ? (h ?? 0) - 12 : (h ?? 0) === 0 ? 12 : h ?? 0;
  return {
    time: `${disp}:${String(m ?? 0).padStart(2, "0")}`,
    ampm
  };
}

// Design-system colour token per category, for the category tag's accent dot.
const CATEGORY_DOT: Record<string, string> = {
  attraction: "bg-bright-blue-500",
  foodDrink: "bg-peach",
  store: "bg-sunshine-yellow-400",
  event: "bg-princess-200",
  hotel: "bg-avocado-300"
};
function categoryDotClass(type: string, isMeal = false): string {
  if (isMeal) return "bg-peach";
  return CATEGORY_DOT[type] ?? "bg-bright-blue-500";
}

/**
 * Category chip built on the design-system Tag (image-overlay variant), with a
 * tokenised colour dot that keeps the planner's at-a-glance category read.
 */
function CategoryTag(t0) {
  const $ = _c(6);
  const {
    label,
    dotClass,
    className
  } = t0;
  const t1 = `size-1.5 shrink-0 rounded-full ${dotClass}`;
  let t2;
  if ($[0] !== t1) {
    t2 = <span aria-hidden={true} className={t1} />;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== className || $[3] !== label || $[4] !== t2) {
    t3 = <Tag variant="transparent-dark" size="sm" className={className}>{t2}{label}</Tag>;
    $[2] = className;
    $[3] = label;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}
const STOP_TYPE_LABEL: Record<string, string> = {
  attraction: "Attraction",
  foodDrink: "Food & drink",
  store: "Shop",
  event: "Event",
  hotel: "Stay"
};
function stopTagLabel(stop: PlannedStop): string {
  if (stop.isMeal && stop.mealType) {
    return stop.mealType.charAt(0).toUpperCase() + stop.mealType.slice(1);
  }
  return STOP_TYPE_LABEL[stop.place.type] ?? "Stop";
}
function planHeadline(plan: DayPlan): string {
  const {
    adults,
    children
  } = plan.inputs.party;
  if (children > 0) return "A day the whole family will remember.";
  if (adults <= 1) return "Your day at the harbour, solo.";
  if (adults === 2) return "An unhurried day for two.";
  return "Your day at the V&A, together.";
}
function subscribeNever() {
  return () => {};
}
interface PlanCalendarContextValue {
  planUrl: string | null;
  onCalendarAdded: () => void;
}
const PlanCalendarContext = createContext<PlanCalendarContextValue>({
  planUrl: null,
  onCalendarAdded: () => {}
});
function buildIcsBlob(plan: DayPlan, stops: PlannedStop[], planUrl: string | null): void {
  const ics = buildPlanIcs(plan, stops, {
    planUrl
  });
  const blob = new Blob([ics], {
    type: "text/calendar;charset=utf-8"
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "va-waterfront-day-plan.ics";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
const HEADER_BG = "https://cdn.sanity.io/images/wrpjy8u8/production/9c13dee176c7dd42242d536592504ac26025cbf3-9504x6336.jpg?w=1600&h=900&q=80&fit=crop&auto=format";
function VaLogo(t0) {
  const $ = _c(6);
  const {
    size: t1
  } = t0;
  const size = t1 === undefined ? 24 : t1;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <circle cx="16" cy="16" r="15" stroke="var(--color-sunshine-yellow-400)" strokeWidth="1" />;
    t3 = <path d="M10 23C10 17 14 9 16 9C18 9 22 17 22 23" stroke="var(--color-sunshine-yellow-400)" strokeWidth="1.4" fill="none" />;
    t4 = <line x1="10" y1="23" x2="22" y2="23" stroke="var(--color-sunshine-yellow-400)" strokeWidth="1" />;
    t5 = <circle cx="16" cy="9" r="1.5" fill="var(--color-sunshine-yellow-400)" />;
    $[0] = t2;
    $[1] = t3;
    $[2] = t4;
    $[3] = t5;
  } else {
    t2 = $[0];
    t3 = $[1];
    t4 = $[2];
    t5 = $[3];
  }
  let t6;
  if ($[4] !== size) {
    t6 = <svg width={size} height={size} viewBox="0 0 32 32" fill="none" aria-hidden={true}>{t2}{t3}{t4}{t5}</svg>;
    $[4] = size;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  return t6;
}

/** Shared action buttons; `pill` rounds them fully (mosaic), else `rounded-xl`. */
function ActionBar(t0) {
  const $ = _c(35);
  const {
    pill,
    planId,
    copied,
    downloadingPdf,
    isModified,
    onDownloadIcs,
    onGoogle,
    onCopy,
    onDownloadPdf,
    onReset,
    onAddToHomeScreen
  } = t0;
  const radius = pill ? "rounded-full" : "rounded-xl";
  const base = `inline-flex items-center gap-2 ${radius} px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.16em] transition-colors`;
  const t1 = `${base} bg-sunshine-yellow-400 text-deep-sea-blue-950 hover:brightness-105`;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <CalendarPlus size={14} />;
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== onDownloadIcs || $[2] !== t1) {
    t3 = <button type="button" onClick={onDownloadIcs} className={t1}>{t2} Add to calendar</button>;
    $[1] = onDownloadIcs;
    $[2] = t1;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = `${base} border border-sunshine-yellow-400/30 bg-deep-sea-blue-900/60 text-cream-100/80 hover:border-sunshine-yellow-400/60`;
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <CalendarPlus size={14} />;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== onGoogle || $[6] !== t4) {
    t6 = <button type="button" onClick={onGoogle} className={t4}>{t5} Google Calendar</button>;
    $[5] = onGoogle;
    $[6] = t4;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== base || $[9] !== copied || $[10] !== onCopy || $[11] !== planId) {
    t7 = planId ? <button type="button" onClick={onCopy} className={`${base} ${"border border-cream-100/20 bg-deep-sea-blue-900/60 text-cream-100/80 hover:border-cream-100/50"}`}>{copied ? <Check size={14} /> : <Link2 size={14} />}{copied ? "Copied" : "Copy link"}</button> : null;
    $[8] = base;
    $[9] = copied;
    $[10] = onCopy;
    $[11] = planId;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== base || $[14] !== onAddToHomeScreen) {
    t8 = onAddToHomeScreen ? <button type="button" onClick={onAddToHomeScreen} className={`${base} ${"border border-cream-100/20 bg-deep-sea-blue-900/60 text-cream-100/80 hover:border-cream-100/50"}`}><Smartphone size={14} /> Add to home screen</button> : null;
    $[13] = base;
    $[14] = onAddToHomeScreen;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const t9 = `${base} ${"border border-cream-100/20 bg-deep-sea-blue-900/60 text-cream-100/80 hover:border-cream-100/50"} disabled:opacity-60`;
  let t10;
  if ($[16] !== downloadingPdf) {
    t10 = downloadingPdf ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />;
    $[16] = downloadingPdf;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  const t11 = downloadingPdf ? "Preparing\u2026" : "Download PDF";
  let t12;
  if ($[18] !== downloadingPdf || $[19] !== onDownloadPdf || $[20] !== t10 || $[21] !== t11 || $[22] !== t9) {
    t12 = <button type="button" onClick={onDownloadPdf} disabled={downloadingPdf} aria-busy={downloadingPdf} className={t9}>{t10}{t11}</button>;
    $[18] = downloadingPdf;
    $[19] = onDownloadPdf;
    $[20] = t10;
    $[21] = t11;
    $[22] = t9;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== base || $[25] !== isModified || $[26] !== onReset) {
    t13 = isModified ? <button type="button" onClick={onReset} className={`${base} ${"border border-cream-100/20 bg-deep-sea-blue-900/60 text-cream-100/80 hover:border-cream-100/50"}`}><RotateCcw size={14} /> Reset</button> : null;
    $[24] = base;
    $[25] = isModified;
    $[26] = onReset;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== t12 || $[29] !== t13 || $[30] !== t3 || $[31] !== t6 || $[32] !== t7 || $[33] !== t8) {
    t14 = <div className="flex flex-wrap gap-2.5">{t3}{t6}{t7}{t8}{t12}{t13}</div>;
    $[28] = t12;
    $[29] = t13;
    $[30] = t3;
    $[31] = t6;
    $[32] = t7;
    $[33] = t8;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  return t14;
}
export function ConciergeBoard({
  plan,
  planId,
  locale,
  variant = "timeline",
  width = "contained",
  onReplan
}: {
  plan: DayPlan;
  planId: string | null;
  locale: string;
  variant?: BoardVariant;
  /** "contained" centres the column (max-w-5xl); "full" spans the card width. */
  width?: "contained" | "full";
  /** Restart the whole planner from the wizard. */
  onReplan?: () => void;
}) {
  const initialStops = useMemo(() => plan.items.filter((i): i is PlannedStop => i.kind === "stop"), [plan.items]);
  const [board, setBoard] = useState<BoardStop[]>(() => toBoardStops(initialStops));
  const [copied, setCopied] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [swapIndex, setSwapIndex] = useState<number | null>(null);
  const [announcement, setAnnouncement] = useState("");
  const [showHomeScreen, setShowHomeScreen] = useState(false);
  const homeScreenPlatform = useHomeScreenPlatform();
  usePlanManifest(planId, locale);
  const origin = useSyncExternalStore(subscribeNever, () => window.location.origin, () => null);
  const planUrl = origin && planId ? `${origin}/${locale}/plan-your-visit/${planId}` : null;
  async function addToHomeScreen() {
    posthog.capture("plan_home_screen_clicked", {
      plan_id: planId
    });
    if (homeScreenPlatform !== "prompt") {
      if (loadedAtCurrentUrl()) setShowHomeScreen(true);else reloadForHomeScreen();
      return;
    }
    if (await promptInstall()) {
      posthog.capture("plan_added_to_home_screen", {
        plan_id: planId
      });
      setShowHomeScreen(false);
    }
  }
  useEffect(() => {
    if (takeHomeScreenReload()) setShowHomeScreen(true);
  }, []);
  function onCalendarAdded() {
    if (!shouldOfferHomeScreen()) return;
    setShowHomeScreen(true);
    posthog.capture("plan_home_screen_prompt_shown", {
      plan_id: planId
    });
  }
  const alternates = plan.alternates ?? [];
  const initialKeys = initialStops.map((s, i_0) => `${s.place.id}-${i_0}`).join("|");
  const isModified = board.map(b => b.key).join("|") !== initialKeys;
  function resetBoard() {
    setBoard(toBoardStops(initialStops));
    setSwapIndex(null);
    setAnnouncement("Plan reset to the original order.");
  }
  function moveStop(from: number, to: number) {
    if (to < 0 || to >= board.length) return;
    const moved = board[from];
    if (!moved) return;
    const next = [...board];
    next.splice(from, 1);
    next.splice(to, 0, moved);
    setBoard(next);
    setAnnouncement(`${moved.stop.place.name} moved to stop ${to + 1} of ${next.length}. Arrival times updated.`);
  }
  function handleSwap(place: PlannablePlace) {
    if (swapIndex === null) return;
    setBoard(prev => {
      const slot = prev[swapIndex];
      if (!slot) return prev;
      const next_0 = [...prev];
      next_0[swapIndex] = {
        key: `${place.id}-swap-${swapIndex}`,
        // Preserve the slot's role (meal/dwell) so pacing holds; times
        // recompute via resequence.
        stop: {
          ...slot.stop,
          place,
          dwellMinutes: place.dwellMinutes?.min ?? slot.stop.dwellMinutes
        }
      };
      return next_0;
    });
    setAnnouncement(`${place.name} swapped in at stop ${swapIndex + 1}. Arrival times updated.`);
    setSwapIndex(null);
  }
  const orderedStops = useMemo(() => resequence(board, plan.inputs.date), [board, plan.inputs.date]);
  const totalDwell = orderedStops.reduce((sum, s_0) => sum + s_0.dwellMinutes, 0);
  const totalWalk = orderedStops.reduce((sum_0, s_1) => sum_0 + (s_1.walkFromPreviousMinutes ?? 0), 0);
  function downloadIcs() {
    buildIcsBlob(plan, orderedStops, planUrl);
    posthog.capture("plan_calendar_downloaded", {
      plan_id: planId
    });
    onCalendarAdded();
  }
  function openGoogleCalendar() {
    const url = googleCalendarUrl(plan, orderedStops, {
      planUrl
    });
    if (!url) return;
    window.open(url, "_blank", "noopener,noreferrer");
    posthog.capture("plan_google_calendar", {
      plan_id: planId
    });
    onCalendarAdded();
  }
  function copyLink() {
    if (!planUrl || !navigator.clipboard) return;
    void navigator.clipboard.writeText(planUrl).then(() => {
      setCopied(true);
      setAnnouncement("Link copied to clipboard.");
      posthog.capture("plan_shared", {
        plan_id: planId
      });
      window.setTimeout(() => setCopied(false), 2000);
    });
  }
  const onDownloadPdf = async () => {
    if (downloadingPdf) return;
    setDownloadingPdf(true);
    try {
      const {
        downloadPlanPdf
      } = await import("@/lib/concierge/download-plan-pdf");
      await downloadPlanPdf({
        plan,
        stops: orderedStops,
        planId,
        locale
      });
      posthog.capture("plan_printed", {
        plan_id: planId
      });
    } catch (err) {
      console.error("[plan-pdf] download failed", err);
    } finally {
      setDownloadingPdf(false);
    }
  };
  const colMax = width === "full" ? "max-w-none" : "max-w-5xl";
  const actionBar = <>
      <ActionBar pill={variant === "mosaic"} planId={planId} copied={copied} downloadingPdf={downloadingPdf} isModified={isModified} onDownloadIcs={downloadIcs} onGoogle={openGoogleCalendar} onCopy={copyLink} onDownloadPdf={onDownloadPdf} onReset={resetBoard} onAddToHomeScreen={planId && homeScreenPlatform ? addToHomeScreen : null} />
      {showHomeScreen && homeScreenPlatform ? <AddToHomeScreen platform={homeScreenPlatform} onClose={() => setShowHomeScreen(false)} onInstalled={() => posthog.capture("plan_added_to_home_screen", {
      plan_id: planId
    })} /> : null}
    </>;
  const replanButton = onReplan ? <span className="dark shrink-0">
      <Button variant="blue-filled" size="sm" asChild className={variant === "mosaic" ? "rounded-full [&_svg]:size-[14px]" : "[&_svg]:size-[14px]"}>
        <button type="button" onClick={onReplan}>
          <RotateCcw size={14} /> Replan
        </button>
      </Button>
    </span> : null;
  return <div className="relative isolate overflow-hidden bg-deep-sea-blue-950 text-cream-100">
      {/* Harbour backdrop — pinned to the top, viewport-height, object-cover
          (no stretch, no repeat). Overlay ramps to a fully-opaque
          deep-sea-blue-950 so it blends into the solid background below. */}
      <div aria-hidden className="absolute inset-x-0 top-0 -z-10 h-screen overflow-hidden">
        <Image src={HEADER_BG} alt="" fill priority className="object-cover ken-burns" sizes="100vw" style={{
        filter: "brightness(0.6)"
      }} />
        <div className="absolute inset-0 bg-gradient-to-b from-deep-sea-blue-950/45 via-deep-sea-blue-950/80 to-deep-sea-blue-950" />
      </div>

      {variant === "mosaic" ? (/* v0 variant-6 header — logo, inline dotted stats */
    <div>
          <div className={`mx-auto ${colMax} px-6 pt-9 pb-10 sm:px-10`}>
            <div data-board-header className="mb-8 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <VaLogo />
                <span className="text-[10px] font-semibold uppercase tracking-[0.28em] text-cream-100/55">
                  V&amp;A Waterfront · Your Day
                </span>
              </div>
              {replanButton}
            </div>
            <div className="grid gap-6 sm:grid-cols-[minmax(0,1fr)_18rem] sm:items-start">
              <div className="min-w-0">
                <Overline variant="on-dark" className="mb-3">
                  Your itinerary · {formatPlanDate(plan.inputs.date)}
                </Overline>
                <h3 className="typography-mobile-heading-h3-voyage desktop:typography-desktop-heading-h3-voyage mb-5 text-balance text-cream-100">
                  {planHeadline(plan)}
                </h3>
                <div className="flex flex-wrap items-center gap-4 text-sm text-cream-100/75">
                  <span>
                    <b className="text-sunshine-yellow-400">
                      {orderedStops.length}
                    </b>{" "}
                    stops
                  </span>
                  <span className="h-1 w-1 rounded-full bg-cream-100/30" />
                  <span>
                    <b className="text-sunshine-yellow-400">
                      {formatMinutes(totalDwell + totalWalk)}
                    </b>{" "}
                    total
                  </span>
                  <span className="h-1 w-1 rounded-full bg-cream-100/30" />
                  <span>
                    <b className="text-sunshine-yellow-400">{totalWalk}</b> min
                    walking
                  </span>
                </div>
              </div>
              <div className="w-full">
                <WeatherWidget date={plan.inputs.date} />
              </div>
            </div>
            <div className="mt-6">{actionBar}</div>
          </div>
        </div>) : (/* Timeline header — gold rule + stat blocks */
    <div className="px-6 pt-9 pb-8 sm:px-10">
          <div className={`mx-auto ${colMax}`}>
            {replanButton ? <div data-board-header className="mb-6 flex justify-end">
                {replanButton}
              </div> : null}
            <div className="grid gap-6 sm:grid-cols-[minmax(0,1fr)_18rem] sm:items-start">
              <div className="min-w-0">
                <Overline variant="on-dark" className="mb-5">
                  Your itinerary · {formatPlanDate(plan.inputs.date)}
                </Overline>
                <h3 className="typography-mobile-heading-h3-voyage desktop:typography-desktop-heading-h3-voyage mb-7 max-w-xl text-balance text-cream-100">
                  {planHeadline(plan)}
                </h3>
                <div className="flex flex-wrap items-end gap-8">
                  {[{
                label: "Stops",
                value: String(orderedStops.length)
              }, {
                label: "Total time",
                value: formatMinutes(totalDwell + totalWalk)
              }, {
                label: "Walking",
                value: `${totalWalk} min`
              }].map(it => <div key={it.label}>
                      <div className="mb-0.5 text-[9px] font-semibold uppercase tracking-[0.22em] text-sunshine-yellow-400/70">
                        {it.label}
                      </div>
                      <div className="text-2xl font-semibold text-cream-100 sm:text-3xl">
                        {it.value}
                      </div>
                    </div>)}
                </div>
              </div>
              <div className="w-full">
                <WeatherWidget date={plan.inputs.date} />
              </div>
            </div>
            <div className="mt-8">{actionBar}</div>
          </div>
        </div>)}

      <p aria-live="polite" aria-atomic="true" className="sr-only">
        {announcement}
      </p>
      <p id="board-reorder-hint" className="sr-only">
        Press the up and down arrow keys to move this stop earlier or later in
        the day.
      </p>

      {variant === "mosaic" ? <div className={`mx-auto ${colMax}`}>
          <PlanCalendarContext.Provider value={{
        planUrl,
        onCalendarAdded
      }}>
            <MosaicCards plan={plan} board={board} setBoard={setBoard} onMove={moveStop} ordered={orderedStops} canSwap={alternates.length > 0} onSwap={i_1 => setSwapIndex(i_1)} />
          </PlanCalendarContext.Provider>
        </div> : <div className={`mx-auto ${colMax}`}>
          <TimelineCards board={board} setBoard={setBoard} onMove={moveStop} ordered={orderedStops} canSwap={alternates.length > 0} onSwap={i_2 => setSwapIndex(i_2)} locale={locale} />
        </div>}

      {swapIndex !== null && orderedStops[swapIndex] ? <SwapModal current={orderedStops[swapIndex]} usedIds={board.map(b_0 => b_0.stop.place.id)} alternates={alternates} locale={locale} onClose={() => setSwapIndex(null)} onPick={handleSwap} /> : null}
    </div>;
}
interface SwapHit {
  objectID: string;
  type?: string;
  title?: string;
  district?: string | null;
  imageUrl?: string;
  imageRef?: string;
  categories?: string[];
}
const SANITY_PROJECT_ID = "wrpjy8u8";
function hitImageUrl(hit: SwapHit): string | undefined {
  if (hit.imageUrl) return hit.imageUrl;
  const m = hit.imageRef ? /^image-([a-f0-9]+)-(\d+x\d+)-(\w+)$/.exec(hit.imageRef) : null;
  if (m) {
    return `https://cdn.sanity.io/images/${SANITY_PROJECT_ID}/production/${m[1]}-${m[2]}.${m[3]}?w=600&h=400&q=80&fit=crop&auto=format`;
  }
  return undefined;
}
function hitToPlace(hit: SwapHit): PlannablePlace {
  const t = hit.type ?? "";
  const type = (["attraction", "foodDrink", "store", "event", "hotel"].includes(t) ? t : "attraction") as PlannablePlace["type"];
  return {
    id: hit.objectID,
    type,
    name: hit.title ?? "Untitled",
    slug: hit.objectID,
    district: hit.district ?? "",
    geopoint: {
      lat: -33.905,
      lng: 18.42
    },
    hours: [],
    closedDates: [],
    dwellMinutes: {
      min: 45,
      max: 90
    },
    tags: [],
    categories: hit.categories ?? [],
    accessibility: {
      wheelchairAccessible: true
    },
    imageUrl: hitImageUrl(hit)
  };
}

/**
 * Category values for the swap modal filter chips. Each place's `categories`
 * already holds the type-appropriate taxonomy (foodDrinkCategory for food,
 * attractionCategory for attractions), so filters read from one consistent
 * dimension — mixing in `cuisines` duplicates buckets (e.g. "Fast Food").
 */
function optionFilterValues(place: PlannablePlace): string[] {
  return place.categories.filter((v): v is string => Boolean(v));
}
function SwapOptionRow(t0) {
  const $ = _c(33);
  const {
    place,
    onPick
  } = t0;
  let t1;
  if ($[0] !== place) {
    t1 = optionFilterValues(place);
    $[0] = place;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const category = t1[0];
  let t2;
  if ($[2] !== onPick || $[3] !== place) {
    t2 = () => onPick(place);
    $[2] = onPick;
    $[3] = place;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== place.imageUrl) {
    t3 = place.imageUrl ? <Image src={place.imageUrl} alt="" fill={true} className="object-cover" sizes="64px" /> : null;
    $[5] = place.imageUrl;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t3) {
    t4 = <span className="relative h-16 w-16 shrink-0 overflow-hidden rounded-lg bg-deep-sea-blue-800">{t3}</span>;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== place.name) {
    t5 = <span className="truncate font-medium text-cream-100">{place.name}</span>;
    $[9] = place.name;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const t6 = STOP_TYPE_LABEL[place.type] ?? "Stop";
  let t7;
  if ($[11] !== place.type) {
    t7 = categoryDotClass(place.type);
    $[11] = place.type;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t6 || $[14] !== t7) {
    t8 = <CategoryTag label={t6} dotClass={t7} />;
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t5 || $[17] !== t8) {
    t9 = <span className="flex flex-wrap items-center gap-2">{t5}{t8}</span>;
    $[16] = t5;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== category || $[20] !== place.district) {
    t10 = place.district || category ? <span className="mt-0.5 block truncate text-xs text-cream-100/50">{[place.district, category].filter(Boolean).join(" \xB7 ")}</span> : null;
    $[19] = category;
    $[20] = place.district;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== place.localTip) {
    t11 = place.localTip ? <span className="mt-1 line-clamp-2 block text-xs leading-snug text-cream-100/70">{place.localTip}</span> : null;
    $[22] = place.localTip;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== t10 || $[25] !== t11 || $[26] !== t9) {
    t12 = <span className="min-w-0 flex-1">{t9}{t10}{t11}</span>;
    $[24] = t10;
    $[25] = t11;
    $[26] = t9;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <span className="mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-sunshine-yellow-400/40 text-sunshine-yellow-400 transition-colors group-hover:bg-sunshine-yellow-400 group-hover:text-deep-sea-blue-950"><Check size={14} /></span>;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== t12 || $[30] !== t2 || $[31] !== t4) {
    t14 = <button type="button" onClick={t2} className="group flex w-full items-start gap-4 rounded-xl border border-cream-100/10 p-3 text-left transition-colors hover:border-cream-100/25 hover:bg-cream-100/5">{t4}{t12}{t13}</button>;
    $[29] = t12;
    $[30] = t2;
    $[31] = t4;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  return t14;
}
function FilterChip(t0) {
  const $ = _c(4);
  const {
    label,
    active,
    onClick
  } = t0;
  const t1 = `shrink-0 rounded-full border px-3 py-1 text-xs font-medium transition-colors ${active ? "border-sunshine-yellow-400 bg-sunshine-yellow-400 text-deep-sea-blue-950" : "border-cream-100/20 text-cream-100/70 hover:border-cream-100/40 hover:text-cream-100"}`;
  let t2;
  if ($[0] !== label || $[1] !== onClick || $[2] !== t1) {
    t2 = <button type="button" onClick={onClick} className={t1}>{label}</button>;
    $[0] = label;
    $[1] = onClick;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
function SwapModal(t0) {
  const $ = _c(51);
  const {
    current,
    usedIds,
    alternates,
    locale,
    onClose,
    onPick
  } = t0;
  let t1;
  if ($[0] !== usedIds) {
    t1 = new Set(usedIds);
    $[0] = usedIds;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const used = t1;
  let t2;
  if ($[2] !== used) {
    t2 = p => !used.has(p.id);
    $[2] = used;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const pool = alternates.filter(t2);
  const swapType = current.place.type === "foodDrink" || current.place.type === "attraction" ? current.place.type : null;
  const typeClause = swapType ? `type:${swapType}` : "type:attraction OR type:foodDrink";
  const [query, setQuery] = useState("");
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [];
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const [hits, setHits] = useState(t3);
  const [filter, setFilter] = useState(null);
  const panelRef = useRef(null);
  const closeRef = useRef(null);
  const [portalTarget] = useState(_temp);
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [];
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  useEffect(_temp2, t4);
  let t5;
  let t6;
  if ($[6] !== onClose) {
    t5 = () => {
      const restoreTo = document.activeElement as HTMLElement | null;
      closeRef.current?.focus();
      const onKey = e => {
        if (e.key === "Escape") {
          e.preventDefault();
          e.stopImmediatePropagation();
          onClose();
          return;
        }
        if (e.key !== "Tab") {
          return;
        }
        const focusables = panelRef.current?.querySelectorAll("a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex=\"-1\"])");
        if (!focusables?.length) {
          return;
        }
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else {
          if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      };
      window.addEventListener("keydown", onKey, {
        capture: true
      });
      return () => {
        window.removeEventListener("keydown", onKey, {
          capture: true
        });
        restoreTo?.focus();
      };
    };
    t6 = [onClose];
    $[6] = onClose;
    $[7] = t5;
    $[8] = t6;
  } else {
    t5 = $[7];
    t6 = $[8];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[9] !== locale || $[10] !== query || $[11] !== typeClause) {
    t7 = () => {
      const q = query.trim();
      let active = true;
      const handle = window.setTimeout(() => {
        const indexName = getAlgoliaIndexName(locale);
        const run = searchClient.search as (requests: unknown) => Promise<{
          results: {
            hits: SwapHit[];
          }[];
        }>;
        run([{
          indexName,
          params: {
            query: q,
            filters: typeClause,
            hitsPerPage: 40
          }
        }]).then(res => {
          if (active) {
            setHits(res.results?.[0]?.hits ?? []);
          }
        }).catch(() => {
          if (active) {
            setHits([]);
          }
        });
      }, q ? 220 : 0);
      return () => {
        active = false;
        window.clearTimeout(handle);
      };
    };
    t8 = [query, typeClause, locale];
    $[9] = locale;
    $[10] = query;
    $[11] = typeClause;
    $[12] = t7;
    $[13] = t8;
  } else {
    t7 = $[12];
    t8 = $[13];
  }
  useEffect(t7, t8);
  const searching = query.trim().length > 0;
  let t9;
  if ($[14] !== used) {
    t9 = p_0 => !used.has(p_0.id);
    $[14] = used;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const hitPlaces = hits.map(hitToPlace).filter(t9);
  let t10;
  if ($[16] !== swapType) {
    t10 = p_1 => swapType ? p_1.type === swapType : p_1.type === "attraction" || p_1.type === "foodDrink";
    $[16] = swapType;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  const curated = pool.filter(t10);
  const tipById = new Map(pool.filter(_temp3).map(_temp4));
  const base = searching ? hitPlaces : curated.length > 0 ? curated : hitPlaces;
  const options = base.map(p_4 => p_4.localTip ? p_4 : {
    ...p_4,
    localTip: tipById.get(p_4.id)
  });
  const filterChips = Array.from(new Set(options.flatMap(optionFilterValues))).sort();
  const activeFilter = filter && filterChips.includes(filter) ? filter : null;
  const shown = activeFilter ? options.filter(p_5 => optionFilterValues(p_5).includes(activeFilter)) : options;
  if (typeof document === "undefined" || !portalTarget) {
    return null;
  }
  const t11 = createPortal;
  const t12 = "fixed inset-0 z-[100] flex items-end justify-center sm:items-center";
  const t13 = "dialog";
  const t14 = "true";
  const t15 = `Change out ${current.place.name}`;
  let t16;
  if ($[18] !== onClose) {
    t16 = <button aria-label="Close" onClick={onClose} className="absolute inset-0 bg-deep-sea-blue-950/80 backdrop-blur-sm" />;
    $[18] = onClose;
    $[19] = t16;
  } else {
    t16 = $[19];
  }
  const t17 = "relative z-10 flex h-[88dvh] w-full flex-col overflow-hidden rounded-t-2xl border border-sunshine-yellow-400/20 bg-deep-sea-blue-900 sm:h-[40rem] sm:max-h-[88dvh] sm:max-w-2xl sm:rounded-2xl";
  let t18;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.26em] text-sunshine-yellow-400">Change out</p>;
    $[20] = t18;
  } else {
    t18 = $[20];
  }
  let t19;
  if ($[21] !== current.place.name) {
    t19 = <div>{t18}<h3 className="typography-mobile-heading-subtitle-1 desktop:typography-desktop-heading-subtitle-1 text-cream-100">{current.place.name}</h3></div>;
    $[21] = current.place.name;
    $[22] = t19;
  } else {
    t19 = $[22];
  }
  let t20;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <X size={16} />;
    $[23] = t20;
  } else {
    t20 = $[23];
  }
  let t21;
  if ($[24] !== onClose) {
    t21 = <button ref={closeRef} type="button" onClick={onClose} aria-label="Close" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-cream-100/20 text-cream-100 transition-colors hover:bg-cream-100/5">{t20}</button>;
    $[24] = onClose;
    $[25] = t21;
  } else {
    t21 = $[25];
  }
  let t22;
  if ($[26] !== t19 || $[27] !== t21) {
    t22 = <div className="flex items-start justify-between gap-4">{t19}{t21}</div>;
    $[26] = t19;
    $[27] = t21;
    $[28] = t22;
  } else {
    t22 = $[28];
  }
  let t23;
  let t24;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <label htmlFor="swap-search" className="sr-only">Search attractions and restaurants</label>;
    t24 = <Search size={15} aria-hidden="true" className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-cream-100/60" />;
    $[29] = t23;
    $[30] = t24;
  } else {
    t23 = $[29];
    t24 = $[30];
  }
  let t25;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = e_0 => setQuery(e_0.target.value);
    $[31] = t25;
  } else {
    t25 = $[31];
  }
  let t26;
  if ($[32] !== query) {
    t26 = <div className="relative mt-5">{t23}{t24}<input id="swap-search" type="search" value={query} onChange={t25} placeholder={"Search attractions & restaurants\u2026"} className="w-full rounded-full border border-cream-100/40 bg-deep-sea-blue-950/60 py-2.5 pl-10 pr-4 text-sm text-cream-100 placeholder:text-cream-100/60 focus-visible:border-sunshine-yellow-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sunshine-yellow-400" /></div>;
    $[32] = query;
    $[33] = t26;
  } else {
    t26 = $[33];
  }
  let t27;
  if ($[34] !== t22 || $[35] !== t26) {
    t27 = <div className="shrink-0 border-b border-cream-100/10 p-6 sm:p-8">{t22}{t26}</div>;
    $[34] = t22;
    $[35] = t26;
    $[36] = t27;
  } else {
    t27 = $[36];
  }
  const t28 = filterChips.length > 1 ? <div className="flex shrink-0 gap-2 overflow-x-auto border-b border-cream-100/10 px-6 py-3 sm:px-8 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"><FilterChip label="All" active={!activeFilter} onClick={() => setFilter(null)} />{filterChips.map(c => <FilterChip key={c} label={c} active={activeFilter === c} onClick={() => setFilter(c)} />)}</div> : null;
  const t29 = "min-h-0 flex-1 overflow-y-auto overscroll-contain p-6 [scrollbar-color:rgba(247,242,231,0.25)_transparent] [scrollbar-width:thin] sm:p-8 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-cream-100/20 hover:[&::-webkit-scrollbar-thumb]:bg-cream-100/35 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar]:w-1.5";
  const t30 = shown.length === 0 ? <p className="py-10 text-center text-sm text-cream-100/50">{searching ? "No matches \u2014 try a different search." : activeFilter ? "No options in this category." : "No other spots available to swap in right now."}</p> : <div className="flex flex-col gap-2">{shown.map(p_6 => <SwapOptionRow key={p_6.id} place={p_6} onPick={onPick} />)}</div>;
  let t31;
  if ($[37] !== t30) {
    t31 = <div className={t29}>{t30}</div>;
    $[37] = t30;
    $[38] = t31;
  } else {
    t31 = $[38];
  }
  let t32;
  if ($[39] !== t27 || $[40] !== t28 || $[41] !== t31) {
    t32 = <div ref={panelRef} className={t17}>{t27}{t28}{t31}</div>;
    $[39] = t27;
    $[40] = t28;
    $[41] = t31;
    $[42] = t32;
  } else {
    t32 = $[42];
  }
  let t33;
  if ($[43] !== t15 || $[44] !== t16 || $[45] !== t32) {
    t33 = <div className={t12} role={t13} aria-modal={t14} aria-label={t15}>{t16}{t32}</div>;
    $[43] = t15;
    $[44] = t16;
    $[45] = t32;
    $[46] = t33;
  } else {
    t33 = $[46];
  }
  let t34;
  if ($[47] !== portalTarget || $[48] !== t11 || $[49] !== t33) {
    t34 = t11(t33, portalTarget);
    $[47] = portalTarget;
    $[48] = t11;
    $[49] = t33;
    $[50] = t34;
  } else {
    t34 = $[50];
  }
  return t34;
}

/* -------------------------------------------------------------------------- */
/* Timeline layout — gold rail + full-bleed cards (v0 Board, our system)      */
/* -------------------------------------------------------------------------- */
function _temp4(p_3) {
  return [p_3.id, p_3.localTip];
}
function _temp3(p_2) {
  return p_2.localTip;
}
function _temp2() {
  const {
    body
  } = document;
  const scrollY = window.scrollY;
  const prev = {
    overflow: body.style.overflow,
    position: body.style.position,
    top: body.style.top,
    width: body.style.width
  };
  const dialog = document.querySelector("[role=\"dialog\"][data-state=\"open\"]");
  const prevDialogOverflow = dialog?.style.overflow;
  body.style.overflow = "hidden";
  body.style.position = "fixed";
  body.style.top = `-${scrollY}px`;
  body.style.width = "100%";
  if (dialog) {
    dialog.style.overflow = "hidden";
  }
  window.scrollTo(0, 0);
  return () => {
    body.style.overflow = prev.overflow;
    body.style.position = prev.position;
    body.style.top = prev.top;
    body.style.width = prev.width;
    if (dialog) {
      dialog.style.overflow = prevDialogOverflow ?? "";
    }
    window.scrollTo(0, scrollY);
  };
}
function _temp() {
  return typeof document === "undefined" ? null : document.querySelector("[role=\"dialog\"][data-state=\"open\"]") ?? document.body;
}
function ReorderHint(t0) {
  const $ = _c(4);
  const {
    className
  } = t0;
  const t1 = `${className} items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.22em] text-cream-100/70`;
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <span aria-hidden="true" className="flex items-center gap-2 [@media(pointer:coarse)]:hidden"><GripVertical size={13} /> Use the grip to reorder stops — arrival times update live</span>;
    t3 = <span className="sr-only">Focus a stop's reorder handle and press the up or down arrow key to reorder stops. Arrival times update live.</span>;
    $[0] = t2;
    $[1] = t3;
  } else {
    t2 = $[0];
    t3 = $[1];
  }
  let t4;
  if ($[2] !== t1) {
    t4 = <p className={t1}>{t2}{t3}</p>;
    $[2] = t1;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  return t4;
}
function TimelineCards(t0) {
  const $ = _c(34);
  const {
    board,
    setBoard,
    onMove,
    ordered,
    canSwap,
    onSwap,
    locale
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <ReorderHint className="mb-7" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="absolute bottom-4 left-[18px] top-4 w-px bg-sunshine-yellow-400/15" />;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== board || $[3] !== canSwap || $[4] !== locale || $[5] !== onMove || $[6] !== onSwap || $[7] !== ordered) {
    let t4;
    if ($[9] !== board.length || $[10] !== canSwap || $[11] !== locale || $[12] !== onMove || $[13] !== onSwap || $[14] !== ordered) {
      t4 = (item, i) => {
        const seq = ordered[i] ?? item.stop;
        return <TimelineRow key={item.key} item={item} index={i} total={board.length} seq={seq} walkToNext={ordered[i + 1]?.walkFromPreviousMinutes ?? 0} canSwap={canSwap} onSwap={() => onSwap(i)} onMove={onMove} locale={locale} />;
      };
      $[9] = board.length;
      $[10] = canSwap;
      $[11] = locale;
      $[12] = onMove;
      $[13] = onSwap;
      $[14] = ordered;
      $[15] = t4;
    } else {
      t4 = $[15];
    }
    t3 = board.map(t4);
    $[2] = board;
    $[3] = canSwap;
    $[4] = locale;
    $[5] = onMove;
    $[6] = onSwap;
    $[7] = ordered;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[16] !== board || $[17] !== setBoard || $[18] !== t3) {
    t4 = <Reorder.Group axis="y" values={board} onReorder={setBoard} role="list" className="flex flex-col">{t3}</Reorder.Group>;
    $[16] = board;
    $[17] = setBoard;
    $[18] = t3;
    $[19] = t4;
  } else {
    t4 = $[19];
  }
  let t5;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="mt-4 flex items-center gap-3"><span className="flex h-9 w-9 items-center justify-center rounded-full border border-dashed border-sunshine-yellow-400/25"><span className="h-2 w-2 rounded-full bg-sunshine-yellow-400/25" /></span><span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-cream-100/60">End of day</span></div>;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  let t6;
  if ($[21] !== t4) {
    t6 = <div className="relative">{t2}{t4}{t5}</div>;
    $[21] = t4;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  let t7;
  if ($[23] !== locale) {
    t7 = <ShoppingPrompt locale={locale} />;
    $[23] = locale;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  const t8 = `/${locale}/visitor-information`;
  let t10;
  let t11;
  let t9;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <LifeBuoy size={20} className="mt-0.5 shrink-0 text-sunshine-yellow-400" />;
    t10 = <span><span className="block text-sm font-semibold text-cream-100">{"Visitor information & emergencies"}</span><span className="mt-1 block text-xs leading-snug text-cream-100/60">Directions, parking, and what to do in an emergency while you’re at the Waterfront.</span></span>;
    t11 = <ArrowUpRight size={16} className="ml-auto shrink-0 text-cream-100/40" />;
    $[25] = t10;
    $[26] = t11;
    $[27] = t9;
  } else {
    t10 = $[25];
    t11 = $[26];
    t9 = $[27];
  }
  let t12;
  if ($[28] !== t8) {
    t12 = <a href={t8} target="_blank" rel="noopener noreferrer" className="mt-4 ml-12 flex items-start gap-3 rounded-2xl border border-cream-100/10 bg-deep-sea-blue-900/40 p-5 transition-colors hover:border-cream-100/25">{t9}{t10}{t11}</a>;
    $[28] = t8;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== t12 || $[31] !== t6 || $[32] !== t7) {
    t13 = <div className="px-4 pb-10 sm:px-8">{t1}{t6}{t7}{t12}</div>;
    $[30] = t12;
    $[31] = t6;
    $[32] = t7;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  return t13;
}
function ShoppingPrompt(t0) {
  const $ = _c(6);
  const {
    locale
  } = t0;
  let t1;
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <ShoppingBag size={20} aria-hidden="true" className="mt-0.5 shrink-0 text-sunshine-yellow-400" />;
    t2 = <h4 className="text-sm font-semibold text-cream-100">Looking for shopping nearby?</h4>;
    t3 = <p className="mt-1 text-xs leading-snug text-cream-100/60">Your plan keeps to activities and dining — stores and visitor services across the Waterfront live on our main site.</p>;
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
  } else {
    t1 = $[0];
    t2 = $[1];
    t3 = $[2];
  }
  const t4 = `/${locale}/stores`;
  let t5;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <ArrowUpRight size={12} />;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] !== t4) {
    t6 = <aside aria-label="Shopping at the Waterfront" className="mt-8 ml-12 flex items-start gap-3 rounded-2xl border border-cream-100/10 bg-deep-sea-blue-900/40 p-5">{t1}<div>{t2}{t3}<a href={t4} target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex items-center gap-1.5 rounded-xl border border-cream-100/30 px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-cream-100 transition-colors hover:bg-cream-100/10">Browse stores {t5}</a></div></aside>;
    $[4] = t4;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  return t6;
}

/**
 * Drag handle with a keyboard-operable equivalent. framer-motion's Reorder has
 * no keyboard support, so arrow keys drive the same reorder as pointer drag.
 */
function ReorderHandle(t0) {
  const $ = _c(10);
  const {
    name,
    index,
    total,
    controls,
    onMove
  } = t0;
  const t1 = `Reorder ${name}, stop ${index + 1} of ${total}`;
  let t2;
  if ($[0] !== controls) {
    t2 = e => controls.start(e);
    $[0] = controls;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== index || $[3] !== onMove) {
    t3 = e_0 => {
      if (e_0.key !== "ArrowUp" && e_0.key !== "ArrowDown") {
        return;
      }
      e_0.preventDefault();
      onMove(index, index + (e_0.key === "ArrowDown" ? 1 : -1));
    };
    $[2] = index;
    $[3] = onMove;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <GripVertical size={18} />;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t1 || $[7] !== t2 || $[8] !== t3) {
    t5 = <button type="button" aria-roledescription="sortable item handle" aria-label={t1} aria-describedby="board-reorder-hint" onPointerDown={t2} onKeyDown={t3} className="cursor-grab touch-none rounded-full p-1 text-cream-100/80 transition-colors hover:text-cream-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sunshine-yellow-400 active:cursor-grabbing">{t4}</button>;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
}
function TimelineRow(t0) {
  const $ = _c(31);
  const {
    item,
    index,
    total,
    seq,
    walkToNext,
    canSwap,
    onSwap,
    onMove,
    locale
  } = t0;
  const controls = useDragControls();
  const reduceMotion = useReducedMotion();
  let t1;
  if ($[0] !== seq.arrivalTime) {
    t1 = to12h(seq.arrivalTime);
    $[0] = seq.arrivalTime;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t = t1;
  let t2;
  if ($[2] !== reduceMotion) {
    t2 = reduceMotion ? {
      zIndex: 10
    } : {
      scale: 1.01,
      zIndex: 10
    };
    $[2] = reduceMotion;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t.time) {
    t3 = <span className="z-10 flex h-9 w-9 items-center justify-center rounded-full border-[1.5px] border-sunshine-yellow-400 bg-deep-sea-blue-800 text-[11px] font-semibold text-sunshine-yellow-400">{t.time}</span>;
    $[4] = t.time;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t.ampm) {
    t4 = <span className="text-[9px] font-semibold uppercase tracking-[0.2em] text-sunshine-yellow-400/70">{t.ampm}</span>;
    $[6] = t.ampm;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t3 || $[9] !== t4) {
    t5 = <div aria-hidden="true" className="mb-3 flex items-center gap-3">{t3}{t4}</div>;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== canSwap || $[12] !== controls || $[13] !== index || $[14] !== locale || $[15] !== onMove || $[16] !== onSwap || $[17] !== seq || $[18] !== total) {
    t6 = <StopCard stop={seq} index={index} total={total} controls={controls} canSwap={canSwap} onSwap={onSwap} onMove={onMove} locale={locale} />;
    $[11] = canSwap;
    $[12] = controls;
    $[13] = index;
    $[14] = locale;
    $[15] = onMove;
    $[16] = onSwap;
    $[17] = seq;
    $[18] = total;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  let t7;
  if ($[20] !== walkToNext) {
    t7 = walkToNext ? <WalkConnector mins={walkToNext} /> : null;
    $[20] = walkToNext;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== t6 || $[23] !== t7) {
    t8 = <div className="pl-12">{t6}{t7}</div>;
    $[22] = t6;
    $[23] = t7;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== controls || $[26] !== item || $[27] !== t2 || $[28] !== t5 || $[29] !== t8) {
    t9 = <Reorder.Item value={item} dragListener={false} dragControls={controls} role="listitem" className="list-none" whileDrag={t2}>{t5}{t8}</Reorder.Item>;
    $[25] = controls;
    $[26] = item;
    $[27] = t2;
    $[28] = t5;
    $[29] = t8;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  return t9;
}
function StopCard(t0) {
  const $ = _c(85);
  const {
    stop,
    index,
    total,
    controls,
    canSwap,
    onSwap,
    onMove,
    locale
  } = t0;
  let t1;
  if ($[0] !== stop.arrivalTime) {
    t1 = to12h(stop.arrivalTime);
    $[0] = stop.arrivalTime;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t = t1;
  const listingHref = stop.place.slug?.startsWith("/") ? `/${locale}${stop.place.slug}` : null;
  let t2;
  if ($[2] !== stop.place.geopoint || $[3] !== stop.place.placeId) {
    t2 = mapsUrl(stop.place.geopoint, stop.place.placeId);
    $[2] = stop.place.geopoint;
    $[3] = stop.place.placeId;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const pinUrl = t2;
  const t3 = `${index * 0.07}s`;
  let t4;
  if ($[5] !== t3) {
    t4 = {
      animationDelay: t3
    };
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== stop.place.imageUrl) {
    t5 = stop.place.imageUrl ? <Image src={stop.place.imageUrl} alt="" fill={true} className="object-cover transition-transform duration-700 group-hover:scale-[1.04]" sizes="(max-width: 768px) 100vw, 640px" style={{
      filter: "brightness(0.55)"
    }} /> : <div className="absolute inset-0 bg-deep-sea-blue-800" />;
    $[7] = stop.place.imageUrl;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="absolute inset-0 bg-gradient-to-t from-deep-sea-blue-950/95 via-deep-sea-blue-950/35 to-transparent" />;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const t7 = index + 1;
  let t8;
  if ($[10] !== t7 || $[11] !== total) {
    t8 = <span className="sr-only">Stop {t7} of {total}</span>;
    $[10] = t7;
    $[11] = total;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  const t9 = String(index + 1);
  let t10;
  if ($[13] !== t9) {
    t10 = t9.padStart(2, "0");
    $[13] = t9;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== t10) {
    t11 = <span aria-hidden="true" className="flex h-9 w-9 items-center justify-center rounded-full bg-sunshine-yellow-400 text-sm font-bold text-deep-sea-blue-950">{t10}</span>;
    $[15] = t10;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] !== stop) {
    t12 = stopTagLabel(stop);
    $[17] = stop;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] !== stop.isMeal || $[20] !== stop.place.type) {
    t13 = categoryDotClass(stop.place.type, stop.isMeal);
    $[19] = stop.isMeal;
    $[20] = stop.place.type;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== t12 || $[23] !== t13) {
    t14 = <CategoryTag label={t12} dotClass={t13} />;
    $[22] = t12;
    $[23] = t13;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] !== controls || $[26] !== index || $[27] !== onMove || $[28] !== stop.place.name || $[29] !== total) {
    t15 = <ReorderHandle name={stop.place.name} index={index} total={total} controls={controls} onMove={onMove} />;
    $[25] = controls;
    $[26] = index;
    $[27] = onMove;
    $[28] = stop.place.name;
    $[29] = total;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== t14 || $[32] !== t15) {
    t16 = <div className="flex items-center gap-2">{t14}{t15}</div>;
    $[31] = t14;
    $[32] = t15;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] !== t11 || $[35] !== t16 || $[36] !== t8) {
    t17 = <div className="flex items-start justify-between">{t8}{t11}{t16}</div>;
    $[34] = t11;
    $[35] = t16;
    $[36] = t8;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  let t18;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <div className="flex-1" />;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  let t19;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <MapPin size={11} />;
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  let t20;
  if ($[40] !== stop.place.district) {
    t20 = <p className="mb-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.22em] text-sunshine-yellow-400/70">{t19} {stop.place.district}</p>;
    $[40] = stop.place.district;
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  let t21;
  if ($[42] !== stop.place.name) {
    t21 = <h4 className="typography-mobile-heading-subtitle-1 desktop:typography-desktop-heading-subtitle-1 mb-2 text-cream-100">{stop.place.name}</h4>;
    $[42] = stop.place.name;
    $[43] = t21;
  } else {
    t21 = $[43];
  }
  let t22;
  if ($[44] !== stop.place.localTip) {
    t22 = stop.place.localTip ? <p className="typography-body-xs-regular mb-3 max-w-md text-cream-100/75">{stop.place.localTip}</p> : null;
    $[44] = stop.place.localTip;
    $[45] = t22;
  } else {
    t22 = $[45];
  }
  let t23;
  if ($[46] !== listingHref || $[47] !== stop.place.name) {
    t23 = listingHref ? <a href={listingHref} target="_blank" rel="noopener noreferrer" onPointerDown={_temp5} aria-label={`View listing for ${stop.place.name}`} className="inline-flex items-center gap-1.5 rounded-xl border border-cream-100/30 px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-cream-100 transition-colors hover:bg-cream-100/10">View listing <ArrowUpRight size={12} /></a> : null;
    $[46] = listingHref;
    $[47] = stop.place.name;
    $[48] = t23;
  } else {
    t23 = $[48];
  }
  let t24;
  if ($[49] !== pinUrl || $[50] !== stop.place.name) {
    t24 = pinUrl ? <a href={pinUrl} target="_blank" rel="noopener noreferrer" onPointerDown={_temp6} aria-label={`Take me to ${stop.place.name} in Google Maps`} className="inline-flex items-center gap-1.5 rounded-xl border border-cream-100/30 px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-cream-100 transition-colors hover:bg-cream-100/10"><Navigation size={11} /> Take me here</a> : null;
    $[49] = pinUrl;
    $[50] = stop.place.name;
    $[51] = t24;
  } else {
    t24 = $[51];
  }
  let t25;
  if ($[52] !== canSwap || $[53] !== onSwap || $[54] !== stop.place.name) {
    t25 = canSwap ? <button type="button" onClick={onSwap} onPointerDown={_temp7} aria-label={`See other options instead of ${stop.place.name}`} className="inline-flex items-center gap-1.5 rounded-xl border border-sunshine-yellow-400/30 px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-sunshine-yellow-400 transition-colors hover:bg-sunshine-yellow-400/10"><RefreshCw size={11} /> See more options</button> : null;
    $[52] = canSwap;
    $[53] = onSwap;
    $[54] = stop.place.name;
    $[55] = t25;
  } else {
    t25 = $[55];
  }
  let t26;
  if ($[56] !== t23 || $[57] !== t24 || $[58] !== t25) {
    t26 = <div className="mb-4 flex flex-wrap items-center gap-2">{t23}{t24}{t25}</div>;
    $[56] = t23;
    $[57] = t24;
    $[58] = t25;
    $[59] = t26;
  } else {
    t26 = $[59];
  }
  let t27;
  if ($[60] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <Clock size={12} />;
    $[60] = t27;
  } else {
    t27 = $[60];
  }
  let t28;
  if ($[61] !== stop.dwellMinutes) {
    t28 = formatMinutes(stop.dwellMinutes);
    $[61] = stop.dwellMinutes;
    $[62] = t28;
  } else {
    t28 = $[62];
  }
  let t29;
  if ($[63] !== t28) {
    t29 = <span className="flex items-center gap-1.5 text-xs text-cream-100/65">{t27} {t28}</span>;
    $[63] = t28;
    $[64] = t29;
  } else {
    t29 = $[64];
  }
  let t30;
  if ($[65] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <span className="h-3 w-px bg-sunshine-yellow-400/20" />;
    $[65] = t30;
  } else {
    t30 = $[65];
  }
  let t31;
  if ($[66] !== t.ampm || $[67] !== t.time) {
    t31 = <span className="text-sm font-semibold text-sunshine-yellow-400">{t.time} {t.ampm}</span>;
    $[66] = t.ampm;
    $[67] = t.time;
    $[68] = t31;
  } else {
    t31 = $[68];
  }
  let t32;
  if ($[69] !== t29 || $[70] !== t31) {
    t32 = <div className="flex items-center gap-4">{t29}{t30}{t31}</div>;
    $[69] = t29;
    $[70] = t31;
    $[71] = t32;
  } else {
    t32 = $[71];
  }
  let t33;
  if ($[72] !== t20 || $[73] !== t21 || $[74] !== t22 || $[75] !== t26 || $[76] !== t32) {
    t33 = <div>{t20}{t21}{t22}{t26}{t32}</div>;
    $[72] = t20;
    $[73] = t21;
    $[74] = t22;
    $[75] = t26;
    $[76] = t32;
    $[77] = t33;
  } else {
    t33 = $[77];
  }
  let t34;
  if ($[78] !== t17 || $[79] !== t33) {
    t34 = <div className="relative z-10 flex min-h-[260px] flex-col p-5 sm:h-full sm:p-6">{t17}{t18}{t33}</div>;
    $[78] = t17;
    $[79] = t33;
    $[80] = t34;
  } else {
    t34 = $[80];
  }
  let t35;
  if ($[81] !== t34 || $[82] !== t4 || $[83] !== t5) {
    t35 = <div className="rise-in group relative min-h-[260px] w-full overflow-hidden rounded-2xl border border-cream-100/5 shadow-[0_12px_36px_rgba(0,0,0,0.5)] sm:aspect-video" style={t4}>{t5}{t6}{t34}</div>;
    $[81] = t34;
    $[82] = t4;
    $[83] = t5;
    $[84] = t35;
  } else {
    t35 = $[84];
  }
  return t35;
}
function _temp7(e_1) {
  return e_1.stopPropagation();
}
function _temp6(e_0) {
  return e_0.stopPropagation();
}
function _temp5(e) {
  return e.stopPropagation();
}
function WalkConnector(t0) {
  const $ = _c(4);
  const {
    mins
  } = t0;
  if (!mins) {
    return null;
  }
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <span className="h-8 w-px" style={{
      background: "repeating-linear-gradient(to bottom, color-mix(in oklab, var(--color-sunshine-yellow-400) 40%, transparent) 0 4px, transparent 4px 8px)"
    }} />;
    t2 = <Footprints size={14} className="text-sunshine-yellow-400/70" />;
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  let t3;
  if ($[2] !== mins) {
    t3 = <div className="flex items-center gap-2 py-3 pl-2">{t1}{t2}<span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-sunshine-yellow-400/70">{mins} min walk</span></div>;
    $[2] = mins;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
}

/* -------------------------------------------------------------------------- */
/* Mosaic layout — magazine hero + reorderable image grid                     */
/* -------------------------------------------------------------------------- */

function MosaicCards(t0) {
  const $ = _c(19);
  const {
    plan,
    board,
    setBoard,
    onMove,
    ordered,
    canSwap,
    onSwap
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <ReorderHint className="mb-6" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== board || $[2] !== canSwap || $[3] !== onMove || $[4] !== onSwap || $[5] !== ordered || $[6] !== plan) {
    let t3;
    if ($[8] !== board.length || $[9] !== canSwap || $[10] !== onMove || $[11] !== onSwap || $[12] !== ordered || $[13] !== plan) {
      t3 = (item, i) => {
        const seq = ordered[i] ?? item.stop;
        return <MosaicCard key={item.key} plan={plan} item={item} index={i} total={board.length} seq={seq} walkToNext={ordered[i + 1]?.walkFromPreviousMinutes ?? 0} canSwap={canSwap} onSwap={() => onSwap(i)} onMove={onMove} />;
      };
      $[8] = board.length;
      $[9] = canSwap;
      $[10] = onMove;
      $[11] = onSwap;
      $[12] = ordered;
      $[13] = plan;
      $[14] = t3;
    } else {
      t3 = $[14];
    }
    t2 = board.map(t3);
    $[1] = board;
    $[2] = canSwap;
    $[3] = onMove;
    $[4] = onSwap;
    $[5] = ordered;
    $[6] = plan;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[15] !== board || $[16] !== setBoard || $[17] !== t2) {
    t3 = <div className="px-4 pb-10 sm:px-8">{t1}<Reorder.Group axis="y" values={board} onReorder={setBoard} role="list" className="flex flex-col">{t2}</Reorder.Group></div>;
    $[15] = board;
    $[16] = setBoard;
    $[17] = t2;
    $[18] = t3;
  } else {
    t3 = $[18];
  }
  return t3;
}
function MosaicCard(t0) {
  const $ = _c(99);
  const {
    plan,
    item,
    index,
    total,
    seq,
    walkToNext,
    canSwap,
    onSwap,
    onMove
  } = t0;
  const controls = useDragControls();
  const reduceMotion = useReducedMotion();
  const stop = seq;
  let t1;
  if ($[0] !== stop.arrivalTime) {
    t1 = to12h(stop.arrivalTime);
    $[0] = stop.arrivalTime;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t = t1;
  const {
    planUrl,
    onCalendarAdded
  } = useContext(PlanCalendarContext);
  let t2;
  if ($[2] !== plan || $[3] !== planUrl || $[4] !== stop) {
    t2 = googleCalendarUrl(plan, [stop], {
      planUrl
    });
    $[2] = plan;
    $[3] = planUrl;
    $[4] = stop;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const stopGoogleUrl = t2;
  let t3;
  if ($[6] !== stop.place.geopoint || $[7] !== stop.place.placeId) {
    t3 = mapsUrl(stop.place.geopoint, stop.place.placeId);
    $[6] = stop.place.geopoint;
    $[7] = stop.place.placeId;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const pinUrl = t3;
  let t4;
  if ($[9] !== reduceMotion) {
    t4 = reduceMotion ? {
      zIndex: 10
    } : {
      scale: 1.01,
      zIndex: 10
    };
    $[9] = reduceMotion;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const t5 = `${index * 0.07}s`;
  let t6;
  if ($[11] !== t5) {
    t6 = {
      animationDelay: t5
    };
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== stop.place.imageUrl) {
    t7 = stop.place.imageUrl ? <Image src={stop.place.imageUrl} alt="" fill={true} className="object-cover transition-transform duration-700 group-hover:scale-105" sizes="(max-width: 768px) 100vw, 640px" style={{
      filter: "brightness(0.6)"
    }} /> : <div className="absolute inset-0 bg-deep-sea-blue-800" />;
    $[13] = stop.place.imageUrl;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <div className="absolute inset-0 bg-gradient-to-t from-deep-sea-blue-950/95 via-deep-sea-blue-950/25 to-transparent" />;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const t9 = index + 1;
  let t10;
  if ($[16] !== t9 || $[17] !== total) {
    t10 = <span className="sr-only">Stop {t9} of {total}</span>;
    $[16] = t9;
    $[17] = total;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  const t11 = String(index + 1);
  let t12;
  if ($[19] !== t11) {
    t12 = t11.padStart(2, "0");
    $[19] = t11;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== t12) {
    t13 = <span aria-hidden="true" className="flex h-10 w-10 items-center justify-center rounded-full bg-sunshine-yellow-400 text-sm font-bold text-deep-sea-blue-950">{t12}</span>;
    $[21] = t12;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] !== t.ampm || $[24] !== t.time) {
    t14 = <span className="rounded-full bg-deep-sea-blue-950/70 px-3 py-1.5 text-[11px] font-semibold text-sunshine-yellow-400 backdrop-blur">{t.time} {t.ampm}</span>;
    $[23] = t.ampm;
    $[24] = t.time;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== t10 || $[27] !== t13 || $[28] !== t14) {
    t15 = <div className="flex items-center gap-2.5">{t10}{t13}{t14}</div>;
    $[26] = t10;
    $[27] = t13;
    $[28] = t14;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] !== controls || $[31] !== index || $[32] !== onMove || $[33] !== stop.place.name || $[34] !== total) {
    t16 = <ReorderHandle name={stop.place.name} index={index} total={total} controls={controls} onMove={onMove} />;
    $[30] = controls;
    $[31] = index;
    $[32] = onMove;
    $[33] = stop.place.name;
    $[34] = total;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== t15 || $[37] !== t16) {
    t17 = <div className="relative z-10 flex items-start justify-between p-5">{t15}{t16}</div>;
    $[36] = t15;
    $[37] = t16;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== stop) {
    t18 = stopTagLabel(stop);
    $[39] = stop;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  let t19;
  if ($[41] !== stop.isMeal || $[42] !== stop.place.type) {
    t19 = categoryDotClass(stop.place.type, stop.isMeal);
    $[41] = stop.isMeal;
    $[42] = stop.place.type;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  let t20;
  if ($[44] !== t18 || $[45] !== t19) {
    t20 = <CategoryTag className="mb-2" label={t18} dotClass={t19} />;
    $[44] = t18;
    $[45] = t19;
    $[46] = t20;
  } else {
    t20 = $[46];
  }
  let t21;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <MapPin size={11} />;
    $[47] = t21;
  } else {
    t21 = $[47];
  }
  const t22 = stop.place.district;
  let t23;
  if ($[48] !== stop.dwellMinutes) {
    t23 = formatMinutes(stop.dwellMinutes);
    $[48] = stop.dwellMinutes;
    $[49] = t23;
  } else {
    t23 = $[49];
  }
  let t24;
  if ($[50] !== stop.place.district || $[51] !== t23) {
    t24 = <p className="mb-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.22em] text-sunshine-yellow-400/70">{t21} {t22} ·{" "}{t23}</p>;
    $[50] = stop.place.district;
    $[51] = t23;
    $[52] = t24;
  } else {
    t24 = $[52];
  }
  let t25;
  if ($[53] !== stop.place.name) {
    t25 = <h4 className="typography-mobile-heading-subtitle-1 desktop:typography-desktop-heading-subtitle-1 mb-2 text-cream-100">{stop.place.name}</h4>;
    $[53] = stop.place.name;
    $[54] = t25;
  } else {
    t25 = $[54];
  }
  let t26;
  if ($[55] !== stop.place.localTip) {
    t26 = stop.place.localTip ? <p className="typography-body-xs-regular mb-3 max-w-md text-cream-100/75">{stop.place.localTip}</p> : null;
    $[55] = stop.place.localTip;
    $[56] = t26;
  } else {
    t26 = $[56];
  }
  let t27;
  if ($[57] !== pinUrl || $[58] !== stop.place.name) {
    t27 = pinUrl ? <a href={pinUrl} target="_blank" rel="noopener noreferrer" aria-label={`Take me to ${stop.place.name} in Google Maps`} className="inline-flex items-center gap-1.5 rounded-full border border-sunshine-yellow-400/40 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-sunshine-yellow-400"><Navigation size={11} /> Take me here</a> : null;
    $[57] = pinUrl;
    $[58] = stop.place.name;
    $[59] = t27;
  } else {
    t27 = $[59];
  }
  let t28;
  if ($[60] !== onCalendarAdded || $[61] !== stop.place.name || $[62] !== stopGoogleUrl) {
    t28 = stopGoogleUrl ? <a href={stopGoogleUrl} onClick={onCalendarAdded} target="_blank" rel="noopener noreferrer" aria-label={`Add ${stop.place.name} to Google Calendar`} className="inline-flex items-center gap-1.5 rounded-full bg-sunshine-yellow-400 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-deep-sea-blue-950"><CalendarPlus size={11} /> Google Cal</a> : null;
    $[60] = onCalendarAdded;
    $[61] = stop.place.name;
    $[62] = stopGoogleUrl;
    $[63] = t28;
  } else {
    t28 = $[63];
  }
  let t29;
  if ($[64] !== onCalendarAdded || $[65] !== plan || $[66] !== planUrl || $[67] !== stop) {
    t29 = () => {
      buildIcsBlob(plan, [stop], planUrl);
      onCalendarAdded();
    };
    $[64] = onCalendarAdded;
    $[65] = plan;
    $[66] = planUrl;
    $[67] = stop;
    $[68] = t29;
  } else {
    t29 = $[68];
  }
  const t30 = `Download ${stop.place.name} as a calendar file`;
  let t31;
  if ($[69] !== t29 || $[70] !== t30) {
    t31 = <button type="button" onClick={t29} aria-label={t30} className="inline-flex items-center gap-1.5 rounded-full border border-sunshine-yellow-400/40 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-sunshine-yellow-400">.ics</button>;
    $[69] = t29;
    $[70] = t30;
    $[71] = t31;
  } else {
    t31 = $[71];
  }
  let t32;
  if ($[72] !== canSwap || $[73] !== onSwap) {
    t32 = canSwap ? <button type="button" onClick={onSwap} onPointerDown={_temp8} className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-cream-100/30 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-cream-100 transition-colors hover:bg-cream-100/10"><RefreshCw size={12} /> Change</button> : null;
    $[72] = canSwap;
    $[73] = onSwap;
    $[74] = t32;
  } else {
    t32 = $[74];
  }
  let t33;
  if ($[75] !== t27 || $[76] !== t28 || $[77] !== t31 || $[78] !== t32) {
    t33 = <div className="flex flex-wrap items-center gap-2">{t27}{t28}{t31}{t32}</div>;
    $[75] = t27;
    $[76] = t28;
    $[77] = t31;
    $[78] = t32;
    $[79] = t33;
  } else {
    t33 = $[79];
  }
  let t34;
  if ($[80] !== t20 || $[81] !== t24 || $[82] !== t25 || $[83] !== t26 || $[84] !== t33) {
    t34 = <div className="absolute inset-x-0 bottom-0 z-10 p-5">{t20}{t24}{t25}{t26}{t33}</div>;
    $[80] = t20;
    $[81] = t24;
    $[82] = t25;
    $[83] = t26;
    $[84] = t33;
    $[85] = t34;
  } else {
    t34 = $[85];
  }
  let t35;
  if ($[86] !== t17 || $[87] !== t34 || $[88] !== t6 || $[89] !== t7) {
    t35 = <div className="rise-in group relative aspect-video overflow-hidden rounded-2xl border border-cream-100/5 shadow-[0_12px_36px_rgba(0,0,0,0.5)]" style={t6}>{t7}{t8}{t17}{t34}</div>;
    $[86] = t17;
    $[87] = t34;
    $[88] = t6;
    $[89] = t7;
    $[90] = t35;
  } else {
    t35 = $[90];
  }
  let t36;
  if ($[91] !== walkToNext) {
    t36 = walkToNext ? <WalkConnector mins={walkToNext} /> : null;
    $[91] = walkToNext;
    $[92] = t36;
  } else {
    t36 = $[92];
  }
  let t37;
  if ($[93] !== controls || $[94] !== item || $[95] !== t35 || $[96] !== t36 || $[97] !== t4) {
    t37 = <Reorder.Item value={item} dragListener={false} dragControls={controls} role="listitem" className="list-none" whileDrag={t4}>{t35}{t36}</Reorder.Item>;
    $[93] = controls;
    $[94] = item;
    $[95] = t35;
    $[96] = t36;
    $[97] = t4;
    $[98] = t37;
  } else {
    t37 = $[98];
  }
  return t37;
}
function _temp8(e) {
  return e.stopPropagation();
}